import {
  UseMutationOptions,
  DefaultOptions,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';

import type { PaginatedResponse } from '@/types/api';

export const queryConfig = {
  queries: {
    // throwOnError: true,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 1000 * 60,
  },
} satisfies DefaultOptions;

export type ApiFnReturnType<FnType extends (...args: any) => Promise<any>> =
  Awaited<ReturnType<FnType>>;

export type QueryConfig<T extends (...args: any[]) => any> = Omit<
  ReturnType<T>,
  'queryKey' | 'queryFn'
>;

export type MutationConfig<
  MutationFnType extends (...args: any) => Promise<any>,
> = UseMutationOptions<
  ApiFnReturnType<MutationFnType>,
  Error,
  Parameters<MutationFnType>[0]
>;

/**
 * Drops an entity from every cached page of a paginated infinite query.
 * Refetching one reloads all of its pages one after another, so a deleted row
 * lingers until the slowest page lands; removing it here retires the row now
 * and lets the refetch reconcile the pages behind it.
 */
export const removeFromInfiniteQueries = <T extends { id: string }>(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  id: string,
) => {
  queryClient.setQueriesData<InfiniteData<PaginatedResponse<T>>>(
    { queryKey },
    (current) =>
      current && {
        ...current,
        pages: current.pages.map((page) => ({
          ...page,
          data: page.data.filter((entity) => entity.id !== id),
        })),
      },
  );
};
